import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getCsrfToken() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)ca_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/** Si falta ca_csrf pero hay sesión, pide una cookie nueva al backend. */
let _csrfPromise = null;
async function ensureCsrfToken(force = false) {
  let token = force ? null : getCsrfToken();
  if (token) return token;
  if (!_csrfPromise) {
    _csrfPromise = axios
      .get(`${BACKEND_URL}/api/auth/csrf`, { withCredentials: true })
      .then((res) => {
        const fromBody = res?.data?.csrf;
        return getCsrfToken() || fromBody || null;
      })
      .catch(() => getCsrfToken())
      .finally(() => { _csrfPromise = null; });
  }
  return _csrfPromise;
}

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Cookie-only auth (S71): never read tokens from localStorage.
// Attach CSRF for mutating requests when cookie is present.

/**
 * Cuántas peticiones hay en vuelo, para que la interfaz pueda avisar cuando
 * algo tarda. Vive aquí porque este cliente es el único punto por el que pasa
 * todo: cualquier pantalla, presente o futura, queda cubierta sin tocarla.
 *
 * Una petición puede marcarse `{ silencioso: true }` cuando es de fondo —un
 * sondeo periódico, por ejemplo— para que no dispare el aviso: nadie está
 * esperando por ella y verla parpadear sola desconcierta.
 */
let _enVuelo = 0;
const _oyentes = new Set();

function _avisar() {
  for (const fn of _oyentes) { try { fn(_enVuelo); } catch { /* un oyente roto no rompe al resto */ } }
}

export function suscribirActividad(fn) {
  _oyentes.add(fn);
  fn(_enVuelo);
  return () => _oyentes.delete(fn);
}

api.interceptors.request.use(async (config) => {
  if (!config.silencioso) { _enVuelo += 1; _avisar(); }
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    let csrf = getCsrfToken();
    if (!csrf) {
      try { csrf = await ensureCsrfToken(); } catch { /* server devolverá 403 claro */ }
    }
    if (csrf) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  // Strip any leftover Authorization from callers
  if (config.headers?.Authorization) {
    delete config.headers.Authorization;
  }
  return config;
});

// '/auth/session/activity' is a normal authenticated endpoint (not an auth
// flow step) — it must go through the same silent refresh-and-retry as any
// other call, or a routine 15-min access-token expiry gets misread as real
// inactivity and force-logs out an actively working user.
const SKIP_REFRESH_URLS = [
  '/auth/me', '/auth/login', '/auth/register', '/auth/refresh', '/auth/logout',
  '/auth/mfa/login-verify', '/auth/forgot-password',
];

let refreshPromise = null;

async function singleFlightRefresh() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .then((res) => res.data)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

function _terminada(config) {
  if (config && !config.silencioso) { _enVuelo = Math.max(0, _enVuelo - 1); _avisar(); }
}

api.interceptors.response.use(
  (res) => { _terminada(res.config); return res; },
  async (error) => {
    _terminada(error.config);
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint = SKIP_REFRESH_URLS.some((url) => requestUrl.includes(url));
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        await singleFlightRefresh();
        const csrf = getCsrfToken() || await ensureCsrfToken();
        if (csrf) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-CSRF-Token'] = csrf;
        }
        return api(originalRequest);
      } catch {
        // session dead — AuthContext / interceptor consumers handle redirect
      }
    }
    const detail = error.response?.data?.detail;
    const isCsrf = error.response?.status === 403 && (
      detail === 'CSRF validation failed'
      || (typeof detail === 'string' && detail.toLowerCase().includes('csrf'))
    );
    if (isCsrf && originalRequest && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;
      try {
        document.cookie = 'ca_csrf=; Max-Age=0; path=/';
        let csrf = await ensureCsrfToken(true);
        if (!csrf) {
          await singleFlightRefresh();
          csrf = getCsrfToken();
        }
        if (csrf) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-CSRF-Token'] = csrf;
        }
        return api(originalRequest);
      } catch {
        /* fallthrough */
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Acepta el `detail` del body, un AxiosError, o un response.data.
 * Socios y otras pantallas a veces pasan el error entero; antes se perdía el mensaje.
 */
export function formatApiError(detail) {
  if (detail == null) return 'Algo salió mal. Intenta de nuevo.';
  // AxiosError o similar { response: { data: { detail } } }
  if (typeof detail === 'object' && detail.response?.data !== undefined) {
    return formatApiError(detail.response.data?.detail ?? detail.response.data);
  }
  if (typeof detail === 'object' && detail.isAxiosError && detail.message) {
    return formatApiError(detail.response?.data?.detail ?? detail.message);
  }
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  if (typeof detail?.detail === 'string') return detail.detail;
  if (typeof detail?.detail !== 'undefined') return formatApiError(detail.detail);
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

/** Clear any pre-S71 credential remnants from localStorage (one-time hygiene). */
export function purgeLegacyAuthStorage() {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  } catch { /* ignore */ }
}

export default api;
